import { useTranslation } from "react-i18next";
import { UnitOutcomeSelector } from "@features/assignments/AssignmentGenerator/components/unit-outcome-selector";
import { UnitTopicSelector } from "@packages/components/unit-topic-selector";
import {
  BaseChatDtoCourse,
  LearningOutcomeDto,
} from "@packages/api/madlenAPI.schemas";
import { CourseData } from "@packages/constant/CourseData";
import { Text } from "@core/text";
interface LessonPlannerLearningOutcomesStepProps {
  gradeLevel: number;
  lesson: string;
  selectedOutcomes: LearningOutcomeDto[];
  onOutcomesChange: (outcomes: LearningOutcomeDto[]) => void;
  isMaarif?: boolean;
}

const LessonPlannerLearningOutcomesStep: React.FC<
  LessonPlannerLearningOutcomesStepProps
> = ({ gradeLevel, lesson, selectedOutcomes, onOutcomesChange, isMaarif }) => {
  const { t } = useTranslation();
  const hasOutcomes = isMaarif
    ? CourseData[lesson as keyof typeof CourseData]?.hasMaarifOutcomes
    : CourseData[lesson as keyof typeof CourseData]?.hasOutcomes;

  const handleOutcomesChange = (
    outcomeIds: number[],
    outcomeTexts?: string[],
  ) => {
    const outcomes = outcomeIds.map((id, index) => ({
      id: id.toString(),
      text: outcomeTexts?.[index] || "",
    }));
    onOutcomesChange(outcomes);
  };

  const handleTopicsChange = (topicIds: number[], topicTexts?: string[]) => {
    const topics = topicIds.map((id, index) => ({
      id: id.toString(),
      text: topicTexts?.[index] || "",
    }));
    onOutcomesChange(topics);
  };
  const isPreSchool = gradeLevel === -3 || gradeLevel === -2;
  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="p-4 sm:p-6">
          <Text variant="h3" className="mb-4">
            {hasOutcomes
              ? t("dailyLessonPlan.learningOutcomes")
              : t("dailyLessonPlan.selectedTopics")}
          </Text>

          {hasOutcomes ? (
            <UnitOutcomeSelector
              grade={isPreSchool ? undefined : gradeLevel}
              courseType={lesson as BaseChatDtoCourse}
              isMaarif={isMaarif || gradeLevel < 5 || isPreSchool}
              selectedOutcomes={selectedOutcomes.map((o) => Number(o.id))}
              setSelectedOutcomes={handleOutcomesChange}
            />
          ) : (
            <UnitTopicSelector
              grade={gradeLevel}
              courseType={lesson as BaseChatDtoCourse}
              selectedTopics={selectedOutcomes.map((o) => Number(o.id))}
              setSelectedTopics={handleTopicsChange}
            />
          )}
        </div>
      </div>

      {/* Helper Text */}
      <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-emerald-400"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
              {t("dailyLessonPlan.learningOutcomesHelper.title")}
            </h3>
            <Text className="mt-2" variant="p" status="success" size="sm">
              dailyLessonPlan.learningOutcomesHelper.description
            </Text>
            <ul className="mt-3 list-disc list-inside text-sm text-emerald-700 dark:text-emerald-300">
              <li>{t("dailyLessonPlan.learningOutcomesHelper.tip1")}</li>
              <li>{t("dailyLessonPlan.learningOutcomesHelper.tip2")}</li>
              <li>{t("dailyLessonPlan.learningOutcomesHelper.tip3")}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LessonPlannerLearningOutcomesStep;
